import Constant from '@/common/constant/Constant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import Utils from '@/common/utils/utils';
import {
    getRichTextFieldContent,
    isTicketCountNumber
} from '@/component/ticketList/handler/TicketHandler';
import { checkDataChangedBeforeChatNowRejectTicket } from '@/component/ticketList/handler/TicketListHandler';
import { bulkCreateTagItem, getFormById, updateFormsData } from '@/service/formManagementService';
import { getProjectById } from '@/service/projectService';
import {
    getCorporationDetailByCorporationDomain
} from '@/service/organizationService';
import { filterTagFieldRequired, getPanelType, filterFieldRequiredForTelephone } from '@/component/ticketList/handler/CreateTicketCommonHandler';
import {
    approveRejectAnonymousQaTicket,
    deleteAllTicketData,
    editTicketData,
    getTicketDetailAPI,
    updateTicketApprove,
    updateTicketInfo,
    uploadTicketFileToS3,
    getTicketById
} from '@/service/ticketService';
import { I18n, Storage } from 'aws-amplify';
import _, { cloneDeep } from 'lodash';
import { hideLoadingMouse } from '../../../common/utils/CustomCursor';
import { createKibanaEditContentOperation } from '@/component/system/logKibanaHandler/LogKibanaHandler';
import { createKibanaLogAPI } from '@/service/logKibanaService';
import LogKibanaConstant from '@/common/constant/LogKibanaConstant';
import { isDisplaySplitPDF } from '@/component/formManagement/handler/FormListHandler';
import { validateExceedSizeOfFieldTicket } from '@/component/common/ValidateTicket';
import { onChangeProjectInuse } from '@/component/multiPanel/handler/WrapPanelHandler';
import { checkModelUnsupport } from '@/component/system/handler/OrganizationProfileHandler';
import TelephoneConstant from '@/common/constant/TelephoneConstant';

/**
 * Check show status field in ticket invitation external Desk, Customer
 * @param {*} detailTicket
 * @returns
 */
export const isShowInvitationStatus = detailTicket => {
    return [
        Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK,
        Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER
    ].includes(detailTicket.ticketType);
};

export const isShowInvitationGuestOrGuestDeskStatus = detailTicket => {
    return [
        Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
        Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT
    ].includes(detailTicket.ticketType);
};

/**
 * Show Message and Button Approved/Rejected with ticket invitation external Desk, Customer
 * @param {*} argsAll
 * @returns
 */
export const isShowInvitationExternalHeader = argsAll => {
    if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER) {
        return isEnableBtnApproveExternalCustomerRequest(argsAll);
    }
    if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK) {
        return isEnableBtnApproveExternalDeskRequest(argsAll);
    }
    return false;
};

/**
 * Handle Show Dialog Approve/Reject with ticket invitation external Desk, Customer
 * @param {*} approveFlag
 * @param {*} argsAll
 * @returns
 */
export const onClickApproveRejectExternal = (approveFlag, argsAll) => {
    if (!approveFlag) return;
    argsAll.setSelectedApproveFlag(approveFlag);
    if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER) {
        argsAll.setIsOpenPopupConfirmApproveRejectCustomerRequest(true);
        return;
    }

    if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK) {
        argsAll.setIsOpenPopupConfirmApproveRejectDeskRequest(true);
        return;
    }
    return;
};

/**
 * onSubmitApproveExternalDeskRequest
 * @param {*} e
 * @param {*} argsAll
 * update ticket detail status approve
 */
export const onSubmitApproveExternalDeskRequest = async (approveFlag, argsAll) => {
    if (
        !approveFlag ||
        ![Constant.APPROVE_FLAG.APPROVE, Constant.APPROVE_FLAG.REJECTED].includes(approveFlag)
    ) {
        return;
    }

    argsAll.setIsLoading(true);
    let ticketId = argsAll.detailTicket.id;
    let ticketType = argsAll.detailTicket.ticketType;

    let requestDto = {
        ticketId,
        corporationId: localStorageUtil.getCorporationDomain(),
        ticketType,
        currentUserMail: argsAll.currentUserDetail.mail,
        approveFlag
    };

    let updateResult = await updateTicketApprove(argsAll.API, requestDto);
    if (updateResult && updateResult.updateStatus) {
        let msgCode = 'USER04MSG028';
        if (approveFlag === Constant.APPROVE_FLAG.REJECTED) {
            msgCode = 'USER04MSG029';
        }
        argsAll.enqueueSnackbar(I18n.get(msgCode), {
            variant: 'success',
            autoHideDuration: 3000
        });
        argsAll.setIsLoading(false);
        argsAll.setTriggerReloadTicket(Date.now().toString());
        argsAll.reloadTicketListByPanelType({
            panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
        });
    } else {
        let msgCode = updateResult?.errorCode || '';
        if (msgCode) {
            if (msgCode === 'COMMSG030') {
                argsAll.setIsOpenPopupApproveRejectError(true);
            } else {
                argsAll.enqueueSnackbar(I18n.get(msgCode), {
                    variant: 'error',
                    autoHideDuration: 3000
                });
            }
        }

        argsAll.setIsLoading(false);
        return;
    }
};

/**
 * isEnableBtnApproveExternalDeskRequest
 * Show/hide btn approve/reject
 * @param {*} argsAll
 * @returns status
 */
export const isEnableBtnApproveExternalDeskRequest = argsAll => {
    let isHelpDeskProject =
        localStorageUtil.getSelectedProjectId() === argsAll.detailTicket.affiliationProject;
    let isInprocessStatus = Constant.APPROVE_FLAG.INPROCESS === argsAll.detailTicket.approveFlag;
    let isApproved = argsAll?.isApprovedExternalDeskRequest || false;
    let isArchived = Constant.LIVE_OR_ARCHIVED.ARCHIVED === argsAll.detailTicket.liveOrArchived;

    return isHelpDeskProject && isInprocessStatus && !isApproved && !isArchived;
};

export const handleArchiveTicket = async (
    API,
    detailTicket,
    setDetailTicket,
    reloadTicketListByPanelType,
    panelTicketListType,
    setTriggerReloadTicket,
    currentUserDetail,
    setTriggerReloadMenu,
    argsAll
) => {
    argsAll.setIsLoading(true);

    let updateData = {
        currentUserMail: currentUserDetail?.mail,
        ticketId: detailTicket?.id,
        corporationId: currentUserDetail?.affiliationOrganization,
        ticketType: detailTicket?.ticketType,
        currentUserId: currentUserDetail?.id,
        updateMethod: Constant.UPDATE_TICKET_METHOD.ARCHIVE_TICKET
    };

    let detailTicketClone = cloneDeep(detailTicket);
    detailTicketClone.liveOrArchived = Constant.LIVE_OR_ARCHIVED.ARCHIVED;
    detailTicketClone.public = Constant.TICKET_PUBLIC.PUBLIC;
    if (detailTicket?.ticketType !== Constant.TICKET_TYPE.EMAIL) {
        setDetailTicket(detailTicketClone);
    }

    // If ticket is ticket notice, archive child first
    if (detailTicket.ticketType === Constant.TICKET_TYPE.BROADCAST_PARENT) {
        await archiveTicketNoticeChild(API, detailTicket);
    }

    let logRequestDto = createKibanaEditContentOperation({
        corporationDetail: argsAll.corporationDetail,
        projectDetail: argsAll.selectedProject,
        userDetail: argsAll.currentUserDetail,
        ticketDetail: argsAll?.detailTicket,
        formDetail: argsAll.formDetail,
        publicRange: ''
    });

    let actionDetail = 'edit(archive)';
    logRequestDto.actionDetail = actionDetail;

   const result = await editTicketData(API, updateData);
   
    if (result?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
        argsAll.setIsLoading(false);
        argsAll.setIsOpenModalReloadPage(true);
        return;
    }

    let param = {
        logType: LogKibanaConstant.CONTENT_OPERATION_ACTION.EDIT,
        logKibanaData: logRequestDto
    };
    if (detailTicket?.ticketType !== Constant.TICKET_TYPE.EMAIL) {
        createKibanaLogAPI(argsAll.API, param);
    } else {
        setDetailTicket(detailTicketClone);
    }

    setTriggerReloadTicket(Date.now().toString());
    argsAll.setReloadChatThread(!argsAll.reloadChatThread);

    argsAll.setIsLoading(false);

    reloadTicketListByPanelType({
        panelType: panelTicketListType
            ? panelTicketListType
            : Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
    });
};

export const handleBackToArrivalTicket = async (
    API,
    detailTicket,
    setDetailTicket,
    reloadTicketListByPanelType,
    panelTicketListType,
    setTriggerReloadTicket,
    currentUserDetail,
    setTriggerReloadMenu,
    setIsLoading,
    setIsOpenModalReloadPage,
    argsAll
) => {
    argsAll.setIsLoading(true);

    let updateData = {
        currentUserMail: currentUserDetail?.mail,
        ticketId: detailTicket?.id,
        corporationId: currentUserDetail?.affiliationOrganization,
        ticketType: detailTicket?.ticketType,
        currentUserId: currentUserDetail?.id,
        ticketChildren: detailTicket?.children,
        updateMethod: Constant.UPDATE_TICKET_METHOD.BACK_TO_ARRIVAL
    };

    let detailTicketClone = cloneDeep(detailTicket);
    detailTicketClone.liveOrArchived = Constant.LIVE_OR_ARCHIVED.LIVE;
    detailTicketClone.public = Constant.TICKET_PUBLIC.PUBLIC;

    setDetailTicket(detailTicketClone);

    const ticketUpdated = await editTicketData(API, updateData);
    if (ticketUpdated?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
        setIsLoading(false);
        setIsOpenModalReloadPage(true);
        return;
    }
    if (!ticketUpdated) {
        argsAll.enqueueSnackbar(I18n.get('COMMSG002'), {
            variant: 'error',
            autoHideDuration: 3000
        });
    }

    setTriggerReloadTicket(Date.now().toString());
    argsAll.setReloadChatThread(!argsAll.reloadChatThread);

    if (detailTicket?.ticketType !== Constant.TICKET_TYPE.EMAIL) {
        argsAll.setIsLoading(false);
    }

    reloadTicketListByPanelType({
        panelType: panelTicketListType
            ? panelTicketListType
            : Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
    });

    argsAll.enqueueSnackbar(I18n.get('MSG-CM-BACK-TO-NEW-SUPPORT-CONTENT-SUCCESSFULLY'), {
        variant: 'success',
        autoHideDuration: 3000
    });
};

/**
 * archiveTicketNoticeChild
 * @param {*} API
 * @param {*} detailTicket
 */
export const archiveTicketNoticeChild = async (API, detailTicket) => {
    let promiseAllUpdateChild = [];

    if (!Utils.isEmptyArray(detailTicket.children)) {
        detailTicket.children.forEach(ticketId => {
            if (ticketId.includes('req')) {
                let dataUpdateChild = {
                    id: ticketId,
                    usage: detailTicket.usage,
                    affiliationOrganization: detailTicket.affiliationOrganization,
                    affiliationProject: detailTicket.affiliationProject,
                    readPermission: detailTicket.readPermission,
                    createdAt: detailTicket.createdAt,
                    liveOrArchived: Constant.LIVE_OR_ARCHIVED.ARCHIVED,
                    public: Constant.TICKET_PUBLIC.PUBLIC
                };
                promiseAllUpdateChild.push(updateTicketInfo(API, dataUpdateChild));
            }
        });
    }

    await Promise.all(promiseAllUpdateChild);
};

const handleEditFileUpload = async (
    fileUploadList,
    updateData,
    setFileUploading,
    setFileUploadList,
    fileUpdatedList
) => {
    let uploadResult = {
        fileListSuccess: [],
        suffixOfAttachments: {}
    };
    let updateFileUploadResult = {};
    if (fileUploadList.length > 0) {
        uploadResult = await uploadTicketFileToS3(
            {
                fileUploadList: fileUploadList,
                affiliationOrganization: updateData.affiliationOrganization,
                fileUpdatedList
            },
            setFileUploading
        );
        // No files are deleted
        if (fileUpdatedList.length === 0) {
            let suffixOfAttachments = uploadResult?.suffixOfAttachments;
            updateFileUploadResult.attachment = JSON.stringify({
                value: [...uploadResult.fileListSuccess]
            });
            updateFileUploadResult.suffixOfAttachments = JSON.stringify({
                value: suffixOfAttachments
            });
            setFileUploadList([]);
        }
        updateFileUploadResult.newFileList = [...uploadResult.fileListSuccess];
    }
    if (fileUpdatedList.length !== 0) {
        let listFileDelete = fileUpdatedList.filter(file => {
            return file.isDelete;
        });
        updateFileUploadResult.listFileDelete = listFileDelete;
        if (listFileDelete.length > 0) {
            listFileDelete.forEach(file => {
                try {
                    Storage.remove(file.fileUrl);
                } catch (e) {
                    console.log('error remove file s3 :>> ', e);
                }
            });
        }
        let existingFileList = fileUpdatedList.filter(file => {
            if (file.isDelete) {
                return false;
            } else {
                return true;
            }
        });
        let suffixOfAttachments = uploadResult?.suffixOfAttachments;
        const newExistingFileList = existingFileList.map(file => {
            delete file.__typename;
            const lastDot = file.fileName.lastIndexOf('.');
            const fileExtension = file.fileName.substring(lastDot + 1).toLowerCase();
            if (suffixOfAttachments[`${fileExtension}`]) {
                suffixOfAttachments[`${fileExtension}`] =
                    suffixOfAttachments[`${fileExtension}`] + 1;
            } else {
                suffixOfAttachments[`${fileExtension}`] = 1;
            }
            return file;
        });

        updateFileUploadResult.attachment = JSON.stringify({
            value: [...uploadResult.fileListSuccess, ...newExistingFileList]
        });
        updateFileUploadResult.suffixOfAttachments = JSON.stringify({
            value: suffixOfAttachments
        });
        setFileUploadList([]);
    }
    return updateFileUploadResult;
};

export const submitUpdateTicket = async (
    API,
    updateData,
    setIsLoading,
    setIsDisplayEditTicket,
    fileUploadList,
    fileUpdatedList,
    setFileUploading,
    setFileUploadList,
    detailTicket,
    fieldListDefaultData,
    formData,
    currentUserDetail,
    editedFieldValue,
    mapDataIdToId,
    isUpdateDraft,
    argsAll
) => {
    let editUploadResult = await handleEditFileUpload(
        fileUploadList,
        updateData,
        setFileUploading,
        setFileUploadList,
        fileUpdatedList
    );

    editedFieldValue[`${mapDataIdToId['attachment']}`] = editUploadResult.attachment;
    editedFieldValue[`${mapDataIdToId['suffixOfAttachments']}`] = editUploadResult.suffixOfAttachments;

    if (
        isUpdateDraft &&
        (detailTicket.ticketType === Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT ||
            detailTicket.ticketType === Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT)
    ) {
        let invitationInfoData = cloneDeep(detailTicket.invitationInfo);
        invitationInfoData.adminEmail = formData.adminMailAdress;
        invitationInfoData.adminFirstName = formData.adminFirstName;
        invitationInfoData.adminLastName = formData.adminLastName;
        invitationInfoData.orgName = formData.guestOrg;
        invitationInfoData.projectName = formData.guestProject;

        let newTicketData = {
            id: detailTicket.id,
            invitationInfo: invitationInfoData
        };

        // Update ticket data
        await updateTicketInfo(API, newTicketData);
    }

    argsAll?.setFileUpdatedList([]);

    // Update hits number of item
    if (!Utils.isEmptyObject(formData)) {
        let oldFieldsData = Utils.convertJSONDataToObject(detailTicket.fields);
        for (const [field, fieldValue] of Object.entries(formData)) {
            let oldValue = !Utils.isEmptyArray(oldFieldsData[field]) ? oldFieldsData[field] : [];
            let newValue = !Utils.isEmptyArray(fieldValue) ? fieldValue : [];
            let fieldDataIndex = Utils.findIndexInArrayById(field, fieldListDefaultData);
            let fieldData = fieldListDefaultData[fieldDataIndex];

            // Update System master
            if (
                fieldDataIndex !== -1 &&
                fieldData.type === Constant.FORM_TYPE.FIELD_TAG &&
                Constant.FORM_DEFINITION.SYSTEM_MASTER_FIELD === fieldData.definition
            ) {
                let newValueId = [];
                if (!Utils.isEmptyArray(newValue)) {
                    newValue.forEach(item => {
                        if (item.value) {
                            newValueId.push(item.value);
                        }
                    });
                }

                if (!isArrayEqual(oldValue, newValueId)) {
                    await updateMasterFieldAfterEditTicket(
                        API,
                        detailTicket,
                        fieldData,
                        oldValue,
                        newValueId,
                        currentUserDetail
                    );
                }
            }
        }
    }
    let submitUserEditTicketRequestDto = {
        ticketId: detailTicket.id,
        currentUserId: currentUserDetail.id,
        updateMethod: Constant.UPDATE_TICKET_METHOD.UPDATE_FIELD_TICKET,
        ticketType: detailTicket.ticketType,
        currentUserMail: currentUserDetail.mail,
        corporationId: currentUserDetail.affiliationOrganization,
        editedFieldValue: JSON.stringify(editedFieldValue),
        updateAttachFile: JSON.stringify({
            newFileList: editUploadResult.newFileList,
            listFileDelete: editUploadResult.listFileDelete
        }),

        mapDataIdToId: JSON.stringify(mapDataIdToId),
        listFieldEdited: JSON.stringify(argsAll.listFieldEdited),
        formId: argsAll.formDetail.id,
        isHelpDesk: argsAll.isHelpDesk,
        isServicesPDFOfCorpOn: isDisplaySplitPDF(argsAll.corporationDetail, argsAll.formDetail?.type),
        isTicketReplyDeliver: argsAll?.isTicketReplyDeliver ? argsAll?.isTicketReplyDeliver : false,
        isEditOriginTicket: argsAll?.isEditOriginTicket ? argsAll?.isEditOriginTicket : false
    };

    let logRequestDto = {};
    if (
        ![Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY].includes(
            detailTicket?.ticketType
        )
    )
        logRequestDto = createKibanaEditContentOperation({
            corporationDetail: argsAll.corporationDetail,
            projectDetail: argsAll.selectedProject,
            userDetail: argsAll.currentUserDetail,
            ticketDetail: argsAll?.detailTicket,
            formDetail: argsAll.formDetail,
            publicRange: ''
        });

    let actionDetail = 'edit';
    if (
        !Utils.isEmptyString(argsAll.newStatusOfTicket) &&
        !Utils.isEmptyObject(argsAll?.statusOfTicket.status.status)
    ) {
        if (argsAll.newStatusOfTicket !== argsAll?.statusOfTicket.status.status) {
            // Prevent status update for TELEPHONE tickets (status field is read-only)
            const isTelephoneTicket = detailTicket?.ticketType === Constant.TICKET_TYPE.TELEPHONE;
            
            if (!isTelephoneTicket) {
                submitUserEditTicketRequestDto.currentStatus = argsAll?.statusOfTicket.status.status;
                submitUserEditTicketRequestDto.newStatus = argsAll?.newStatusOfTicket;
                actionDetail = 'edit(changeStatus:{status})';
            }
        }
    }

    if (
        !Utils.isEmptyString(argsAll.newAssignee) &&
        argsAll?.newAssignee !== argsAll?.detailTicket?.personInChargeInfo?.id
    ) {
        submitUserEditTicketRequestDto.currentAssigneeId =
            argsAll?.detailTicket?.personInChargeInfo?.id;
        submitUserEditTicketRequestDto.newAssigneeId = argsAll?.newAssignee;
        if (Utils.isEmptyString(argsAll?.detailTicket?.personInChargeInfo?.id) || argsAll?.newAssignee === Constant.OPTION_UNASSIGNEE.KEY) {
            const isSystemForm = argsAll.formDetail?.definition === Constant.FORM_DEFINITION.SYSTEM
            actionDetail = isSystemForm ? 'edit' : 'edit(changeStatus:{status})';
        }
    }
    logRequestDto.actionDetail = actionDetail;
    const resUserEditTicket = await editTicketData(API, submitUserEditTicketRequestDto);
    let param = {
        logType: LogKibanaConstant.CONTENT_OPERATION_ACTION.EDIT,
        logKibanaData: logRequestDto
    };

    if (
        ![Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY].includes(
            detailTicket?.ticketType
        )
    )
        createKibanaLogAPI(argsAll.API, param);

    if (resUserEditTicket?.errorCode === 'COMMSG030') {
        argsAll.setIsOpenPopupEditError(true);
        argsAll.setIsLoading(false);
        return { isConfirm: true };
    }
    if (resUserEditTicket?.wasUpdatedBefore) {
        argsAll.setForceEditTicketObject({
            method: Constant.UPDATE_TICKET_METHOD.FORCE_EDIT_ASSIGNEE_AND_STATUS,
            requestDto: {
                ...submitUserEditTicketRequestDto
            }
        });
        argsAll.setIsLoading(false);
        return { isConfirm: true };
    }
    setIsDisplayEditTicket(false);
    argsAll.setIsMe(false);
};

export const submitSoftDeleteTicket = async (
    API,
    detailTicket,
    deletedAt,
    currentUserDetail,
    argsAll
) => {
    let submitSoftDeleteTicketRequestDto = {
        ticketId: detailTicket.id,
        currentUserId: currentUserDetail.id,
        currentUserMail: currentUserDetail.mail,
        corporationId: currentUserDetail.affiliationOrganization,
        updateMethod: Constant.UPDATE_TICKET_METHOD.UPDATE_DELETED_AT,
        isHelpDesk: argsAll.isHelpDesk,
        ticketType: detailTicket.ticketType,
        deletedAt: deletedAt
    };
    return await editTicketData(API, submitSoftDeleteTicketRequestDto);
};

export const submitRestoreAnonymousTicket = async (
    API,
    detailTicket,
    anonymousQaStatus,
    currentUserDetail,
    argsAll
) => {
    let submitRestoreAnonymousTicketRequestDto = {
        ticketId: detailTicket.id,
        currentUserId: currentUserDetail.id,
        currentUserMail: currentUserDetail.mail,
        corporationId: currentUserDetail.affiliationOrganization,
        updateMethod: Constant.UPDATE_TICKET_METHOD.UPDATE_ANONYMOUS_STATUS,
        isHelpDesk: argsAll.isHelpDesk,
        ticketType: detailTicket.ticketType,
        approveFlag: anonymousQaStatus
    };
    return await editTicketData(API, submitRestoreAnonymousTicketRequestDto);
};

/**
 * isArrayEqual
 * compare two arr
 * @param {*} x
 * @param {*} y
 * @returns
 */
const isArrayEqual = function(x, y) {
    return _(x)
        .differenceWith(y, _.isEqual)
        .isEmpty();
};

/**
 * updateMasterFieldAfterEditTicket
 * @param {*} API
 * @param {*} detailTicket
 * @param {*} fieldData
 * @param {*} oldValue
 * @param {*} newValue
 */
export const updateMasterFieldAfterEditTicket = async (
    API,
    detailTicket,
    fieldData,
    oldValue,
    newValue,
    currentUserDetail
) => {
    let fieldUpdateData = await getFormById(API, fieldData.id);

    if (!Utils.isEmptyObject(fieldUpdateData)) {
        let fieldUpdateStructure = Utils.convertJSONDataToObject(fieldUpdateData.structure);
        let cloneFieldUpdateStructure = cloneDeep(fieldUpdateStructure);
        let cloneItemList = cloneDeep(cloneFieldUpdateStructure.listItem);
        let newItemList = [];

        cloneItemList.forEach(item => {
            let itemId = item.itemId;
            let newItemData = cloneDeep(item);
            if (oldValue.includes(itemId)) {
                newItemData.hitNumber = item.hitNumber - 1;
                newItemData.listTicketId = item.listTicketId.filter(
                    ticketId => ticketId !== detailTicket.id
                );
            }

            if (newValue.includes(itemId)) {
                let newListTicketId = newItemData.listTicketId;
                newItemData.hitNumber = newItemData.hitNumber + 1;
                newListTicketId.push(detailTicket.id);
                newItemData.listTicketId = newListTicketId;
            }

            newItemData.hitNumber = newItemData.hitNumber < 0 ? 0 : newItemData.hitNumber;
            newItemList.push(newItemData);
        });

        cloneFieldUpdateStructure.listItem = newItemList;

        await updateFormsData(API, fieldUpdateData.id, {
            structure: JSON.stringify(cloneFieldUpdateStructure),
            updator: currentUserDetail.id
        });
    }
};

export const updateTabNameAfterEditTicket = (argsAll, newOutline, panelId, ticketId) => {
    if (argsAll?.setPanelList) {
        argsAll?.setPanelList(prev => {
            let currentPanelList = cloneDeep(prev);
            if (Utils.isEmptyArray(currentPanelList)) return [];
            let currentPanelIndex = Utils.findIndexInArrayById(panelId, currentPanelList);
            if (currentPanelIndex === -1) return;
    
            let currentPanel = currentPanelList[currentPanelIndex];
    
            if (Utils.isEmptyArray(currentPanel.tabList)) return;
            let currentTabIndex = Utils.findIndexInArrayById(ticketId, currentPanel.tabList);
            if (currentTabIndex === -1) return;
    
            // Update ticket outline
            currentPanelList[currentPanelIndex].tabList[currentTabIndex].ticketOutline = newOutline;
    
            return currentPanelList
        });
    }
};

/**
 * handleEditTicketWhenSentFromDraft
 * @param {*} formData
 * @param {*} argsAll
 */
export const handleEditTicketWhenSentFromDraft = async (formData, argsAll) => {
    let fields = Utils.getEditTicketData(formData, argsAll.fieldListDefaultData);

    // validate exceed max size
    if (validateExceedSizeOfFieldTicket(argsAll.fieldListDefaultData, argsAll.setFieldListDefaultData, fields, argsAll.setErrorMessageHeader)) {
        argsAll.setIsLoading(false);
        return;
    }

    let tagField = fields.tagField;
    if (!Utils.isEmptyObject(tagField)) {
        for (const fieldId in tagField) {
            let listNewItem = [];
            for (const item of tagField[fieldId]) {
                if (item.isNewItem) {
                    const parentField = argsAll.fieldListDefaultData.find(
                        field => field.fieldId === fieldId
                    );
                    const itemData = {
                        usage: Constant.FORM_USAGE.TAG,
                        name: JSON.stringify({
                            jp: {
                                A: 'F',
                                text: item.title
                            }
                        }),
                        affiliationOrganization: parentField.affiliationOrganization,
                        affiliationProject: parentField.affiliationProject,
                        affiliationField: parentField.id,
                        creator: argsAll.currentUserDetail.id,
                        updator: argsAll.currentUserDetail.id,
                        type: '',
                        structure: JSON.stringify({
                            isShowItemInTicket: true,
                            no: item.no
                        }),
                        viewForCustomer: JSON.stringify({}),
                        definition: Constant.FORM_DEFINITION.USER,
                        explanation: JSON.stringify({}),
                        usingProjects: parentField.usingProjects,
                        hitNumber: 0,
                        hasValue: true
                    };
                    listNewItem.push(itemData);
                }
            }
            const listNewId = await bulkCreateTagItem(argsAll.API, listNewItem);
            fields[fieldId].push(...listNewId);
            //fields[fieldId] = JSON.stringify(fields[fieldId])
        }
    }
    delete fields.tagField;
    let newOutline = JSON.stringify(formData?.outline ? { jp: formData.outline } : {});
    let updateData = {
        id: argsAll.detailTicket.id,
        affiliationOrganization: argsAll.detailTicket.affiliationOrganization
    };
    
    let editedFieldValue = Utils.getFieldsValueEdited(formData, fields, argsAll.mapDataIdToId);
    editedFieldValue = await Utils.putFieldDataToS3BeforeEditTicket(editedFieldValue, argsAll.mapDataIdToId, argsAll.fieldListDefaultData, argsAll.detailTicket);

    await submitUpdateTicket(
        argsAll.API,
        updateData,
        argsAll.setIsLoading,
        argsAll.setIsDisplayEditTicket,
        argsAll.fileUploadList,
        argsAll.fileUpdatedList,
        argsAll.setFileUploading,
        argsAll.setFileUploadList,
        argsAll.detailTicket,
        argsAll.fieldListDefaultData,
        formData,
        argsAll.currentUserDetail,
        editedFieldValue,
        argsAll.mapDataIdToId,
        true,
        argsAll
    );
    let newTicketDetail = await getTicketDetailAPI(argsAll.detailTicket.id);
    if (!Utils.isEmptyArray(newTicketDetail?.settingTitle)) {
        newOutline = getTitle(newTicketDetail?.settingTitle);
    }
    updateTabNameAfterEditTicket(argsAll, newOutline, argsAll.panelId, argsAll.detailTicket.id);
    argsAll.setIsEdited(false);
    argsAll?.setListFieldEdited([]);
};

export const isInputAllFieldRequired = (fieldListData, getValuesCreateTicket, fileUploadList) => {
    let listFieldRequired = fieldListData?.filter(field => field.structure.required);
    if (!Utils.isEmptyArray(listFieldRequired)) {
        let isInputFieldRequired = true;

        for (let i = 0; i < listFieldRequired.length; i++) {
            let field = listFieldRequired[i];
            let fieldValue = getValuesCreateTicket(field.id);

            // if (Utils.isEmpty(fieldValue) || fieldValue === '') {
            //     isInputFieldRequired = false;
            //     break;
            // } else {
            //     isInputFieldRequired = true;
            // }

            if (field.type === Constant.FORM_TYPE.FIELD_RICHTEXT) {
                if (!fieldValue) {
                    return false;
                }
                let textInput = getRichTextFieldContent(fieldValue);
                if (!textInput) {
                    isInputFieldRequired = false;
                    break;
                }
            } else {
                if (field.sysField === 'attachments') {
                    if (!Utils.isEmptyArray(fileUploadList)) {
                        isInputFieldRequired = true;
                    } else {
                        isInputFieldRequired = false;
                    }
                    break;
                } else if (
                    Utils.isEmpty(fieldValue) ||
                    fieldValue === '' ||
                    (field.type === Constant.FORM_TYPE.FIELD_TAG && Utils.isEmptyArray(fieldValue))
                ) {
                    isInputFieldRequired = false;
                    break;
                }
            }
        }

        return isInputFieldRequired;
    }

    return true;
};

/**
 * rejectAnonymousQaTicket
 * @param {*} ticket
 * @param {*} argsAll
 * @returns
 */
export const rejectAnonymousQaTicket = (ticket, argsAll) => {
    if (Utils.isEmptyObject(ticket) || !Utils.isAnonymousQAWaitingTicket(ticket)) {
        return;
    }

    argsAll.setIsConfirmRejectTicket(true);
};

export const confirmForceEditTicket = async (argsAll, method, requestDto) => {
    argsAll.setIsLoading(true);
    await editTicketData(argsAll.API, {
        ...requestDto,
        forceUpdate: true
    });
    argsAll.setIsLoading(false);
    argsAll.setIsDisplayEditTicket(false);
    argsAll.setIsEdited(false);
    argsAll?.setListFieldEdited([]);
    argsAll.setTriggerReloadTicket(Date.now().toString());
    if (
        Utils.isAnonymousQATicketFromLiveChat(argsAll.detailTicket) &&
        argsAll.newStatusData.no === Constant.STATUS_NO.DONE
    ) {
        argsAll.reloadPanelAfterSoftDeleteTicketByPanelType({
            panelType: Constant.PANEL_TYPE.HELPDESK_CHATTHREAD,
            id: argsAll.detailTicket.id
        });
    }
    argsAll.reloadTicketListByPanelType({
        panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
    });
};

/**
 * submitRejectAnonymousQaTicket
 * @param {*} argsAll
 * @returns
 */
export const submitRejectAnonymousQaTicket = async argsAll => {
    if (
        Utils.isEmptyObject(argsAll.detailTicket) ||
        !Utils.isAnonymousQAWaitingTicket(argsAll.detailTicket)
    )
        return;

    argsAll.setIsLoading(true);
    let rejectRequestDto = {
        currentUserMail: argsAll.currentUserDetail?.mail,
        ticketId: argsAll.detailTicket?.id,
        corporationId: localStorageUtil.getAffiliationOrganization(),
        ticketType: argsAll.detailTicket?.ticketType,
        approveFlag: Constant.APPROVE_FLAG.REJECTED,
        currentUserId: argsAll.currentUserDetail?.id
    };

    let rejectTicket = await approveRejectAnonymousQaTicket(argsAll.API, rejectRequestDto);
    if (!rejectTicket || !rejectTicket.id) {
        argsAll.setIsOpenModalApproveRejectError(true);
        argsAll.setIsLoading(false);
        return;
    }

    argsAll.setIsLoading(false);
    if (rejectTicket.wasUpdatedBefore) {
        argsAll.setApprovalFlagAfterUpdated(rejectTicket.anonymousQaStatus);
        // argsAll.setIsTicketUpdatedBefore(true);
        argsAll.setIsOpenModalApproveRejectError(true);
        return;
    }

    // Reload list helpdesk
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST });

    //reload panel chat thread
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_CHATTHREAD });

    // Close tab ticket detail
    argsAll.closePanelTab({
        event: null,
        panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL,
        panelId: argsAll.panelId,
        tabId: argsAll.detailTicket.id
    });
};

export const submitAcceptAnonymousQaTicket = async argsAll => {
    argsAll.setIsLoading(true);
    let approveRequestDto = {
        currentUserMail: argsAll.currentUserDetail?.mail,
        ticketId: argsAll.detailTicket?.id,
        corporationId: localStorageUtil.getAffiliationOrganization(),
        ticketType: argsAll.detailTicket?.ticketType,
        approveFlag: Constant.APPROVE_FLAG.APPROVE,
        currentUserId: argsAll.currentUserDetail?.id
    };

    let approveTicket = await approveRejectAnonymousQaTicket(argsAll.API, approveRequestDto);
    const detailTicket = {...argsAll?.detailTicket}
    let logRequestDto = createKibanaEditContentOperation({
        corporationDetail: argsAll.corporationDetail,
        projectDetail: argsAll.selectedProject,
        userDetail: argsAll.currentUserDetail,
        ticketDetail: argsAll?.detailTicket,
        formDetail: argsAll.formDetail,
        publicRange: ''
    });
    let statusOfTicket = Utils.convertJSONDataToObject(detailTicket.status);
    let actionDetail = 'edit'
    if(!Utils.isEmptyObject(statusOfTicket) && !Utils.isEmptyString(statusOfTicket?.workflow)){
        actionDetail =  'edit(changeStatus:{status})'
    }
    logRequestDto.actionDetail = actionDetail
    let param = {
        logType: LogKibanaConstant.CONTENT_OPERATION_ACTION.EDIT,
        logKibanaData: logRequestDto
    };
    createKibanaLogAPI(argsAll.API, param);
    if (!approveTicket || !approveTicket.id) {
        argsAll.setIsLoading(false);
        argsAll.setIsOpenModalApproveRejectError(true);
        return;
    }
    if (approveTicket.wasUpdatedBefore) {
        argsAll.setIsLoading(false);
        argsAll.setApprovalFlagAfterUpdated(approveTicket.anonymousQaStatus);
        // argsAll.setIsTicketUpdatedBefore(true);
        argsAll.setIsOpenModalApproveRejectError(true);
        return;
    }
    // Reload list helpdesk
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST });

    //reload panel chat thread
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_CHATTHREAD });

    // Reload ticket detail panel
    argsAll.setTriggerReloadTicket(Date.now().toString());
};

export const approveAnonymousQaTicket = argsAll => {
    if (
        Utils.isEmptyObject(argsAll.detailTicket) ||
        !Utils.isAnonymousQAWaitingTicket(argsAll.detailTicket)
    )
        return;
    argsAll.setIsConfirmAcceptTicket(true);
};

export const reloadAfterApproveAnonymousTicket = argsAll => {
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST });

    // Reload ticket detail panel
    argsAll.setTriggerReloadTicket(Date.now().toString());
};

export const reloadAfterRejectAnonymousTicket = argsAll => {
    argsAll.reloadTicketListByPanelType({ panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST });

    // Close tab ticket detail
    argsAll.closePanelTab({
        event: null,
        panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL,
        panelId: argsAll.panelId,
        tabId: argsAll.detailTicket.id
    });
};

/**
 *
 * isEnableBtnApproveExternalCustomerRequest
 * @param {*} argsAll
 * @returns
 */
export const isEnableBtnApproveExternalCustomerRequest = argsAll => {
    let ticketProjectTargetList = !Utils.isEmptyArray(argsAll.detailTicket.projectTarget)
        ? argsAll.detailTicket.projectTarget
        : [];
    let isCustomerProject = ticketProjectTargetList.includes(argsAll.selectedProject.id)
        ? true
        : false;
    let isInprocessStatus = Constant.APPROVE_FLAG.INPROCESS === argsAll.detailTicket.approveFlag;
    let isApproved = argsAll?.isApprovedExternalCustomerRequest || false;
    let isArchived = Constant.LIVE_OR_ARCHIVED.ARCHIVED === argsAll.detailTicket.liveOrArchived;

    return isCustomerProject && isInprocessStatus && !isApproved && !isArchived;
};

/**
 * onSubmitApproveExternalCustomerRequest
 * approve/reject external customer request
 * @param {*} e
 * @param {*} argsAll
 * @returns
 */
export const onSubmitApproveExternalCustomerRequest = async (approveFlag, argsAll) => {
    if (
        !approveFlag ||
        ![Constant.APPROVE_FLAG.APPROVE, Constant.APPROVE_FLAG.REJECTED].includes(approveFlag)
    ) {
        return;
    }

    argsAll.setIsLoading(true);
    let ticketId = argsAll.detailTicket.id;
    let ticketType = argsAll.detailTicket.ticketType;

    let requestDto = {
        ticketId,
        corporationId: localStorageUtil.getCorporationDomain(),
        ticketType,
        currentUserMail: argsAll.currentUserDetail.mail,
        approveFlag,
        currentUserId: argsAll.currentUserDetail.id
    };

    let updateResult = await updateTicketApprove(argsAll.API, requestDto);
    if (updateResult && updateResult.updateStatus) {
        let msgCode = 'USER04MSG028';
        if (approveFlag === Constant.APPROVE_FLAG.REJECTED) {
            msgCode = 'USER04MSG029';
        }
        argsAll.enqueueSnackbar(I18n.get(msgCode), {
            variant: 'success',
            autoHideDuration: 3000
        });
        argsAll.setIsLoading(false);
        argsAll.setTriggerReloadTicket(Date.now().toString());
        argsAll.reloadTicketListByPanelType({
            panelType: Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
        });
    } else {
        let msgCode = updateResult?.errorCode || '';
        if (msgCode) {
            if (msgCode === 'COMMSG030') {
                argsAll.setIsOpenPopupApproveRejectError(true);
            } else {
                argsAll.enqueueSnackbar(I18n.get(msgCode), {
                    variant: 'error',
                    autoHideDuration: 3000
                });
            }
        }

        argsAll.setIsLoading(false);
        return;
    }
};

/**
 * isDisplayIconChat
 * @param {*} argsAll
 * @returns
 */
export const isDisplayIconChat = argsAll => {
    if (!argsAll?.detailTicket) return false;

    if (argsAll?.detailTicket?.public === Constant.TICKET_PUBLIC.PUBLIC) {
        if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.TASK) {
            return true;
        }

        if (argsAll?.detailTicket?.ticketType === Constant.TICKET_TYPE.TELEPHONE) {
            const metaData = Utils.convertJSONDataToObject(argsAll?.detailTicket?.metaData);
            const currentUserId = argsAll?.currentUserDetail?.id || localStorageUtil.getUserDetailId();
            const selectedProjectId = argsAll?.selectedProject?.id || localStorageUtil.getSelectedProjectId();

            if (metaData?.callType === TelephoneConstant.CALL_TYPE.VOICE_CALL) {
                const voiceFlow = Utils.defineVoiceCallFlow(metaData);
                if (
                    voiceFlow === TelephoneConstant.VOICE_CALL_FLOW.USER_TO_PROJECT ||
                    voiceFlow === TelephoneConstant.VOICE_CALL_FLOW.PROJECT_TO_USER
                ) {
                    return argsAll?.detailTicket?.affiliationProject === selectedProjectId ||
                        argsAll?.detailTicket?.projectOrigin === selectedProjectId ||
                        argsAll?.detailTicket?.customer === currentUserId
                }
                
                if (voiceFlow === TelephoneConstant.VOICE_CALL_FLOW.PROJECT_TO_PROJECT) {
                    return argsAll?.detailTicket?.affiliationProject === selectedProjectId
                }
                
                return false;
            }
            
            if (
                // For normal: phone call, training ...
                argsAll?.detailTicket?.affiliationProject === selectedProjectId ||
                argsAll?.detailTicket?.customer === currentUserId
            ) {
                return true;
            }
        }
    }

    if (
        [
            Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER,
            Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
            Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT,
            Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK
        ].includes(argsAll.detailTicket?.ticketType) ||
        argsAll?.viewFromProjectOfEscalateTicket
    ) {
        return false;
    }

    if (Utils.isAnonymousQATicketFromLiveChat(argsAll?.detailTicket)) {
        return true;
    }

    if (
        !argsAll?.isAnonymousQAWaitingTicket &&
        !Utils.isAnonymousQARejectTicket(argsAll?.detailTicket) &&
        argsAll?.detailTicket?.public !== Constant.TICKET_PUBLIC.DRAFT
    ) {
        return true;
    }

    return false;
};

export const isDisplayIconKnowledge = (argsAll, projectIdListUserIsB2C) => {
    // Exclude telephone ticket from displaying knowledge list option
    if (argsAll.detailTicket?.ticketType === Constant.TICKET_TYPE.TELEPHONE) {
        return false;
    }
    
    if (argsAll.userPermissionView.type !== Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER) {
        if (argsAll?.detailTicket.knowledgeService === Constant.STATUS_SERVICE_PERMISSION.OFF) {
            return false;
        }
        if (
            (argsAll.corporationDetail?.knowledgeService ===
                Constant.STATUS_SERVICE_PERMISSION.OFF &&
                argsAll.selectedProject?.id === argsAll.detailTicket?.affiliationProject) ||
            Utils.isAnonymousQARejectTicket(argsAll.detailTicket) ||
            [
                Constant.TICKET_TYPE.BROADCAST_PARENT,
                Constant.TICKET_TYPE.BROADCAST_CHILD,
                Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
                Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT,
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK,
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER
            ].includes(argsAll.detailTicket?.ticketType)
        ) {
            return false;
        }

        // Disable when user is not B2C of project
        if (argsAll.currentUserDetail && Utils.isB2CUser(argsAll.currentUserDetail)) {
            if (Utils.isEmptyArray(projectIdListUserIsB2C) || !projectIdListUserIsB2C.includes(argsAll.detailTicket?.affiliationProject)) return false;
        }

        return true;
    } else {
        if (argsAll.userPermissionView.knowledgeService !== Constant.STATUS_SERVICE_PERMISSION.ON) {
            return false;
        }
        return true;
    }
};

export const showUpdatorDetailTicket = (isHelpDesk, detailTicket) => {
    if (
        detailTicket.ticketType === Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT &&
        Utils.isExpired(detailTicket.invitationInfo.expireAt) &&
        (detailTicket.invitationFlag === Constant.INVITATION_FLAG.WAITING_APPROVE ||
            detailTicket.invitationFlag === Constant.INVITATION_FLAG.INPROCESS)
    ) {
        return Constant.UPDATOR_SYSTEM;
    }
    if (
        Utils.isAnonymousQAWaitingTicket(detailTicket) ||
        Utils.isAnonymousQATicketFromLiveChat(detailTicket)
    ) {
        return detailTicket.updatorUserName
            ? detailTicket.updatorUserName
            : detailTicket.createrUserName;
    }

    if (
        isHelpDesk &&
        Utils.isAnonymousQAWaitingTicket(detailTicket) &&
        detailTicket.customer !== Constant.ANONYMOUS_USER_ID &&
        detailTicket.creator === detailTicket.updator
    ) {
        return detailTicket.createrUserName;
    }

    return Utils.getUpdatedTimeTicket(isHelpDesk, detailTicket).updatorUserName;
};

export const getTitle = settingTitle => {
    let title = '';

    settingTitle?.forEach(element => {
        if (!element.isBreak) {
            title = title + element.text;
        }
    });
    return title;
};

export const handlerShowButtonEditTicket = (value, allArgs, formData) => {
    let cloneFieldListDefaultData = cloneDeep(allArgs.fieldListDefaultData);
    // filter field is required
    let realRequiredFieldList = cloneFieldListDefaultData.filter(
        field =>
            field.sysField !== Constant.TICKET_DATA_ID.TICKET_ID &&
            !Utils.isEmptyObject(field) &&
            field?.structure?.required
    );

    if (Utils.isEmptyArray(realRequiredFieldList)) {
        allArgs.setIsEditTicket(true);
        return;
    }

    // let formData = getValuesEditTicket();
    //get all fields edit
    let fields = Utils.getEditTicketData(formData, allArgs.fieldListDefaultData);
    let valueTags = value;
    let result = false;
    let newFieldList = [];

    // Assign edit value to each required field
    for (let i = 0; i < realRequiredFieldList.length; i++) {
        let element = realRequiredFieldList[i];
        for (const property in fields) {
            if (element.id === property) {
                element.realValue = fields[property];
                newFieldList.push(element);
            }
        }
    }

    // Check if each required field has enough data
    let arrayResult = [];
    for (let index = 0; index < newFieldList.length; index++) {
        let element = newFieldList[index];
        if (
            [
                Constant.FIELD_UI.TEXT,
                Constant.FIELD_UI.NUMBER,
                Constant.FIELD_UI.TEXT_AREA,
                Constant.FIELD_UI.DATE,
                Constant.FIELD_UI.IFRAME,
                Constant.FIELD_UI.URL
            ].includes(element.structure.UI)
        ) {
            if (element?.realValue.toString().trim() !== '') {
                arrayResult.push(true);
                continue;
            }
            arrayResult.push(false);
            break;
        }
        if (Constant.FIELD_UI.FIELD_RICHTEXT === element.structure.UI) {
            const richTextValue = getRichTextFieldContent(element?.realValue)
            let isInputFieldRequired = true;
            if (!richTextValue || richTextValue === '""') {
                isInputFieldRequired = false;
            }

            arrayResult.push(isInputFieldRequired);
        }
    }

    // validate in DetailTicket.js
    // if (newFieldList.find(item => item.structure.UI === Constant.FIELD_UI.DROP_FILE)) {
    //     if (allArgs.fileUpdatedList.length > 0 || allArgs.fileUploadList.length > 0) {
    //         arrayResult.push(true);
    //     } else {
    //         arrayResult.push(false);
    //     }
    // }

    if (newFieldList.find(item => item.structure.UI === Constant.FIELD_UI.TAG)) {
        if (!allArgs.isTagList) {
            arrayResult.push(true);
        } else if (allArgs.tagListValue.length > 0 && !valueTags) {
            arrayResult.push(true);
        } else if (valueTags && valueTags.length > 0) {
            arrayResult.push(true);
        } else {
            arrayResult.push(false);
        }
    }

    // Check data and set button display save
    if (!arrayResult.includes(false)) {
        result = true;
    }
    allArgs.setIsEditTicket(result);
};

export const getTicketListType = () => {
    return getPanelType() === Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL
        ? Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
        : Constant.PANEL_TYPE.HELPDESK_TICKET_LIST;
};

export const checkErrorDetailTicketQAFromLiveChat = async (API, id, currentTicket) => {
    let ticket = await getTicketById(API, id);
    if (!ticket || Utils.isEmptyObject(ticket) || (ticket && ticket.id === null)) {
        return true;
    }
    if (currentTicket.anonymousQaStatus !== ticket.anonymousQaStatus) {
        if (
            ticket.anonymousQaStatus ===
            Constant.ANONYMOUS_TICKET_STATUS.END_LIVE_CHAT_AGENT_NOT_RESPONSE
        ) {
            return false;
        }
        return true;
    }

    return false;
};

export const handleDeleteCompleteTicketLiveChat = async (
    API,
    setIsLoading,
    detailTicket,
    enqueueSnackbar,
    setCorporationDetail,
    closePanelTab,
    corporationDetail,
    panelId,
    ticketListType,
    panelType,
    tabId,
    triggerReload,
    panelList,
    setIsShowDialogDeleteTicketFromLiveChat,
    reloadTicketListByPanelType,
    setIsOpenModalReloadPage,
    currentProjectId,
    currentProject
) => {
    let checkDataChanged = await checkDataChangedBeforeChatNowRejectTicket(
        API,
        currentProjectId,
        currentProject
    );
    if (!checkDataChanged.status) {
        setIsOpenModalReloadPage(true);
        setIsShowDialogDeleteTicketFromLiveChat(false);
        return;
    }
    setIsLoading(true);
    setIsShowDialogDeleteTicketFromLiveChat(false);
    let ticketId = detailTicket.id;
    let isDataChanged = await checkErrorDetailTicketQAFromLiveChat(API, ticketId, detailTicket);
    if (isDataChanged) {
        setIsOpenModalReloadPage(true);
        setIsShowDialogDeleteTicketFromLiveChat(false);
        return;
    }

    let removeOldTicket = await deleteAllTicketData(API, {
        ticketId: ticketId,
        type: Constant.REMOVE_TICKET_TYPE.LIVE_CHAT
    });

    if (!removeOldTicket) {
        setIsLoading(false);
        setIsShowDialogDeleteTicketFromLiveChat(false);
        enqueueSnackbar(I18n.get('COMMSG002'), {
            variant: 'error',
            autoHideDuration: 3000
        });
        return;
    }

    if (
        isTicketCountNumber(detailTicket.ticketType) &&
        detailTicket.affiliationOrganization === corporationDetail.id
    ) {
        let corpDetailResult = await getCorporationDetailByCorporationDomain(
            API,
            corporationDetail.organizationDomain
        );
        setCorporationDetail(corpDetailResult);
    }

    //reload panel helpdesk list
    reloadTicketListByPanelType({
        panelType: Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
    });

    setIsLoading(false);

    // triggerReload(Date.now().toString());

    closePanelTab({
        event: null,
        panelType: getPanelType(),
        panelId: panelId,
        tabId: detailTicket.public === Constant.TICKET_PUBLIC.PUBLIC ? ticketId : tabId
    });

    // Remove work space ticket detail
    let currentPanelList = cloneDeep(panelList);
    let helpDeskDetailTicketPanel = currentPanelList.find(
        panel => panel.type === Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
    );
    if (!Utils.isEmptyObject(helpDeskDetailTicketPanel)) {
        if (!Utils.isEmptyArray(helpDeskDetailTicketPanel.tabList)) {
            let tabTicketDetail = helpDeskDetailTicketPanel.tabList.find(
                tab => tab.id === detailTicket.id
            );
            if (!Utils.isEmptyObject(tabTicketDetail)) {
                closePanelTab({
                    event: null,
                    panelType: helpDeskDetailTicketPanel.type,
                    panelId: helpDeskDetailTicketPanel.id,
                    tabId: tabTicketDetail.id
                });
            }
        }
    }

    closePanelTab({
        event: null,
        panelType:
            ticketListType === 'helpdesk' ||
            panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
                ? Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
                : Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD,
        panelId: panelId,
        tabId: detailTicket.public === Constant.TICKET_PUBLIC.PUBLIC ? ticketId : tabId
    });

    hideLoadingMouse();
};

/**
 * isDisplayButtonCompleteDeleteTicket
 * @param {*} detailTicket
 * @returns
 */
export const isDisplayButtonCompleteDeleteTicket = detailTicket => {
    return (
        Utils.isAnonymousQATicketFromLiveChat(detailTicket) &&
        (Utils.isAnonymousQALiveChatTicket(detailTicket)
        )
    );
};

export const isDisableButtonSoftDeleteTicketQAAnonymos = detailTicket => {
    if (Utils.isAnonymousQAEndLiveChatTicket(detailTicket) && !detailTicket.deletedAt) {
        return true;
    }
    return false;
};

/**
 * isDisableButtonCompleteDeleteTicket
 * @param {*} detailTicket
 * @returns
 */
export const isDisableButtonCompleteDeleteTicket = detailTicket => {
    if (Utils.isAnonymousQAEndLiveChatTicket(detailTicket)) {
        return false;
    }

    return true;
};

export const openPanelByTicketDetail = async (
    detailTicket,
    projectId,
    addNewPanel,
    props,
    setLastUserAction,
    targetProjectId,
    targetThreadId,
    targetChatPostDetail
) => {
    if (Utils.isEmptyObject(detailTicket) || !projectId) return;
    if (targetThreadId) {
        props.selectThreadId = targetThreadId;
    }

    const needProjectSwitch = targetProjectId !== projectId && !Utils.isEmptyString(targetProjectId);

    if (needProjectSwitch) {
        try {
            const targetProjectDetail = await getProjectById(null, targetProjectId);
            !!targetProjectDetail && await onChangeProjectInuse(props, targetProjectDetail);
        } catch (error) {
            console.log('openShareLink onChangeProjectInuse error: ', error)
        }
    }

    const DELAY_MILLISECOND_NEED_TO_CHANGE_PROJECT = needProjectSwitch ? 3000 : 0;
    const DELAY_MILLISECOND_TO_OPEN_CHAT = 2500;
    
    setTimeout(() => {
        let ticketOutline = '';
        if (!Utils.isEmptyObject(detailTicket) && detailTicket.settingTitle) {
            ticketOutline = getTitle(detailTicket.settingTitle);
        }

        let isCustomer = detailTicket?.affiliationProject !== projectId;
        if (props?.openPanelType) {
            isCustomer = props.openPanelType !== Constant.TYPE_GET_TICKET_LIST.HELPDESK;
        }
        
        let panelType = Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL;

        if([Constant.TICKET_TYPE.KNOWLEDGE, Constant.TICKET_TYPE.MASTER_KNOWLEDGE].includes(
            detailTicket?.ticketType
        )){
            panelType = Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL;
        }

        // ticket type only display in helpdesk list.
        if ([Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY, Constant.TICKET_TYPE.TASK].includes(detailTicket?.ticketType)) {
            panelType = Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL;
            props = { ...props, targetTicketIdOfChatPost: targetChatPostDetail?.ticketId };
        }

        // ticket type can be displayed in qa or helpdesk list by permission of ticket affiliationProject with projectInuse
        const isTelephoneInList = [
            Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
            Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT,
            Constant.TICKET_TYPE.BROADCAST_PARENT,
            Constant.TICKET_TYPE.BROADCAST_CHILD,
            Constant.TICKET_TYPE.NOTICE,
            Constant.TICKET_TYPE.QA,
            Constant.TICKET_TYPE.INTERNAL_QA,
            Constant.TICKET_TYPE.EXTERNAL_QA,
            Constant.TICKET_TYPE.ESCALATION,
            Constant.TICKET_TYPE.DELEGATE,
            Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK,
            Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER,
            Constant.TICKET_TYPE.TELEPHONE
        ].includes(detailTicket.ticketType);

        if(isTelephoneInList){
            panelType = isCustomer ?  Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL : Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL;
            if (detailTicket.ticketType === Constant.TICKET_TYPE.BROADCAST_CHILD) {
                props = { ...props, ticketListType: Constant.TYPE_GET_TICKET_LIST.CUSTOMER };
            }

            props = { ...props, panelType }; 
        }

        // 
        const newPanelId = Date.now().toString();
        let objAddNewPanel = {
            type: panelType,
            tabId: detailTicket.id,
            props: {
                ...props,
                ticketInfo: detailTicket
            },
            ticketId: detailTicket.id,
            isCustomer: isCustomer,
            isTicketDeleted: !Utils.isEmptyString(detailTicket?.deletedAt) ? true : false,
            isEmailTicket: [Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY].includes(detailTicket?.ticketType),
            isTelephoneTicket: detailTicket?.ticketType === Constant.TICKET_TYPE.TELEPHONE,
            newPanelId: newPanelId,
        };
        if (setLastUserAction) {
            setLastUserAction(LogKibanaConstant.CONTENT_OPERATION_ACTION.VIEW);
        }

        if (ticketOutline) {
            objAddNewPanel.ticketOutline = JSON.stringify({ jp: ticketOutline });
            objAddNewPanel.props.ticketOutline =  JSON.stringify({ jp: ticketOutline });
        }

        objAddNewPanel.isDraft =
            detailTicket.public === Constant.TICKET_PUBLIC.DRAFT &&
            Utils.isEmpty(detailTicket.deletedAt);

        if ([Constant.TICKET_TYPE.PDF_FILE_KNOWLEDGE, Constant.TICKET_TYPE.PDF_PAGE_KNOWLEDGE].includes(detailTicket.ticketType)) {
            objAddNewPanel.isPDFKnowledge = true;
            objAddNewPanel.ticketOutline = ticketOutline;
        }

        if (
            ![Constant.TICKET_TYPE.PDF_FILE_KNOWLEDGE, Constant.TICKET_TYPE.MASTER_KNOWLEDGE].includes(
                detailTicket.ticketType
            )
        ) {
            objAddNewPanel.isOpenChat = isDisplayIconChat({
                detailTicket,
            });
        }
        
        addNewPanel(objAddNewPanel);

        /**
         * open chat, condition for ticket and knowledge.
         */
        if (
            (([
                Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL,
                Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
            ].includes(panelType) &&
                isDisplayIconChat({
                    detailTicket
                })) ||
            (panelType === Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL &&
                !Utils.isEmptyString(targetThreadId))) && 
                !Utils.isAnonymousQAWaitingTicket(detailTicket) &&
                !Utils.isAnonymousQARejectTicket(detailTicket)
        ) {
            /**
             * selected chat thread by panel type of ticket detail.
             */
            let chatThreadPanelType =''
            switch (panelType) {
                case Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL:
                    chatThreadPanelType = Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;
                    break;
                case Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL:
                    chatThreadPanelType = Constant.PANEL_TYPE.HELPDESK_CHATTHREAD;
                    break;
                case Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL:
                    chatThreadPanelType = Constant.PANEL_TYPE.KNOWLEDGE_CHATTHREAD;
                    break;
                default:
                    chatThreadPanelType = Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;
            }

            /**
             * timeout to open chat thread after render ticket detail panel.
             */
            setTimeout(() => {
                let propsClone = {
                    API: props?.API,
                    tabId: detailTicket?.id,
                    ticketId: detailTicket?.id,
                    panelType: panelType
                };

                const openThreadChatParam = {
                    type: chatThreadPanelType,
                    tabId: detailTicket.id,
                    currentPanelId: newPanelId,
                    isTicketDeleted: !Utils.isEmptyString(detailTicket?.deletedAt) ? true : false,
                    isShowAllChatPost: false,
                    props: propsClone,
                    ticketOutline: ticketOutline,
                    ticketId: detailTicket?.id,
                    openRelatePanelType: panelType,
                    isEmailTicket: detailTicket?.ticketType === Constant.TICKET_TYPE.EMAIL
                };

                if(!Utils.isEmptyString(targetThreadId)){
                    openThreadChatParam.props.selectThreadId = targetThreadId;
                    openThreadChatParam.selectThreadId = targetThreadId;
                }
                addNewPanel(openThreadChatParam);
            }, DELAY_MILLISECOND_NEED_TO_CHANGE_PROJECT + DELAY_MILLISECOND_TO_OPEN_CHAT);
        }
    }, DELAY_MILLISECOND_NEED_TO_CHANGE_PROJECT)
};


/**
 * first is validate for attachment field
 * other field no bug
 * isInputAllFieldRequiredWhenEditTicket
 * @param {*} fieldListData 
 * @param {*} fileUploadList 
 */
export const isInputAllFieldRequiredWhenEditTicket = (fieldListData, fileUploadList = [], getValuesEditTicket = () => {}, aiAutoSelectedData = {}, listChatGptModel = [], sdautoSelectedData = {}, aiTelephoneSelectedData = {}) => {
    let listFieldRequired = fieldListData?.filter(field => field.structure.required && field.type !== Constant.FORM_TYPE.FIELD_WORKFLOW);
    // filter tag field not show - use filterFieldRequiredForTelephone for AI Telephone form
    let isTelephoneForm = fieldListData?.some(field => field.sysField?.startsWith(Constant.ID_PREFIX.AI_TELEPHONE_FIELD_ID_PREFIX));
    if (isTelephoneForm) {
        listFieldRequired = filterFieldRequiredForTelephone(listFieldRequired, aiTelephoneSelectedData);
    } else {
        listFieldRequired = filterTagFieldRequired(listFieldRequired, aiAutoSelectedData, sdautoSelectedData);
    }

    let currentFileListOfTicket = fileUploadList.filter(file => file && !file.isDelete);
    let allFieldValue = getValuesEditTicket();
     
    if (!Utils.isEmptyArray(listFieldRequired)) {
        let isInputFieldRequired = true;

        for (let field of listFieldRequired) {
            if (field.sysField === Constant.SYS_FIELD.ATTACHMENTS) {
                if (!Utils.isEmptyArray(currentFileListOfTicket)) {
                    isInputFieldRequired = true;
                } else {
                    isInputFieldRequired = false;
                }
                break;
            }

            if (field.type === Constant.FORM_TYPE.FIELD_TAG) {
                if (!allFieldValue || !allFieldValue[field.id] || Utils.isEmptyArray(allFieldValue[field.id])) {
                    isInputFieldRequired = false;
                    break;
                }
                if (field?.sysField === Constant.AI_AUTO.SYSTEM_FIELD.CHATGPT_MODEL) {
                    let isValid = checkModelUnsupport(allFieldValue[field.id][0].title, listChatGptModel)
                    if (isValid) {
                        isInputFieldRequired = false;
                        break;
                    }
                }
            }
        }

        return isInputFieldRequired;
    }

    return true;
}

export const renderUserName = (detailTicket) => {
    let userName = '';
    let lastName = '';
    let firstName = '';
    if (!Utils.isEmptyString(detailTicket?.anonymousUserInfo?.lastName)) {
        lastName = detailTicket?.anonymousUserInfo?.lastName;
    }
    if (!Utils.isEmptyString(detailTicket?.anonymousUserInfo?.firstName)) {
        firstName = detailTicket?.anonymousUserInfo?.firstName;
    }
    userName = `${lastName} ${firstName}`.trim();
    return userName;
}

/**
 * Check if value is a valid telephone call status
 * @param {string} value - Value to check
 * @returns {boolean} True if value is a telephone call status
 */
const isTelephoneCallStatusValue = (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }
    const telephoneCallStatusValues = Object.values(TelephoneConstant.TELEPHONE_CALL_STATUS);
    return (
        telephoneCallStatusValues.includes(value) ||
        value.startsWith(Constant.ID_PREFIX.TELEPHONE_STATUS_PREFIX)
    );
};

/**
 * Extract field value from field object
 * Handles both JSON string and direct string values
 * @param {string} fieldValue - Field value (can be JSON string or direct string)
 * @returns {string|null} Extracted value or null
 */
const extractFieldValue = (fieldValue) => {
    if (!fieldValue || Utils.isEmptyString(fieldValue)) {
        return null;
    }
    try {
        const parsedValue = Utils.convertJSONDataToObject(fieldValue);
        if (parsedValue && parsedValue.value) {
            return parsedValue.value;
        }
    } catch (e) {
        // If parsing fails, use value directly
    }
    return fieldValue;
};

/**
 * Find telephone call status field from field list
 * @param {Array} fieldList - List of fields
 * @returns {Object|null} Telephone call status field or null
 */
const findTelephoneCallStatusField = (fieldList) => {
    if (Utils.isEmptyArray(fieldList)) {
        return null;
    }
    return (
        fieldList.find(
            field => field?.sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_STATUS
        ) || null
    );
};

/**
 * Get telephone status from direct ticket properties
 * Read from telephoneStatusDetail
 * @param {Object} ticket - Ticket object
 * @returns {string|null} Telephone status or null
 */
const getTelephoneStatusFromDirectProperties = (ticket) => {
    if (ticket.telephoneStatusDetail) {
        let telephoneStatusDetail = Utils.convertJSONDataToObject(ticket.telephoneStatusDetail);
        if (telephoneStatusDetail?.sysField) {
            return telephoneStatusDetail?.sysField;
        }
    }
    return null;
};

/**
 * Get telephone status from fieldListData
 * Extract value from ticketData array or field defaultValue/value
 * @param {Object} ticket - Ticket object
 * @returns {string|null} Telephone status or null
 */
const getTelephoneStatusFromFieldListData = (ticket) => {
    if (!ticket.fieldListData && !ticket.listFieldData) {
        return null;
    }
    const fieldList =
        Utils.convertJSONDataToObject(ticket.fieldListData) || ticket.listFieldData || [];
    if (Utils.isEmptyArray(fieldList)) {
        return null;
    }
    const telephoneCallStatusField = findTelephoneCallStatusField(fieldList);
    if (!telephoneCallStatusField) {
        return null;
    }
    // Get value from ticketData array if available
    if (ticket.ticketData && !Utils.isEmptyArray(ticket.ticketData)) {
        const fieldData = ticket.ticketData.find(
            item => item.dataID === telephoneCallStatusField.id
        );
        if (fieldData) {
            const fieldValue = Utils.getValueFromTicketData(fieldData);
            if (fieldValue && !Utils.isEmptyString(fieldValue)) {
                return fieldValue;
            }
        }
    }
    // Get from defaultValue or value of field
    const defaultValue = extractFieldValue(telephoneCallStatusField.defaultValue);
    if (defaultValue) {
        return defaultValue;
    }
    return extractFieldValue(telephoneCallStatusField.value);
};

/**
 * Get telephone status from fields object when fieldListData is not available
 * Search through all field values to find telephone call status pattern
 * @param {Object} fieldsObj - Fields object (parsed from ticket.fields)
 * @returns {string|null} Telephone status or null
 */
const getTelephoneStatusFromFieldsObjectWithoutFieldList = (fieldsObj) => {
    const excludedKeys = [
        Constant.SYS_FIELD.SUFFIX_OF_ATTACHMENTS,
        Constant.SYS_FIELD.MEMO,
        Constant.SYS_FIELD.DESCRIPTION
    ];
    for (const [fieldId, fieldValue] of Object.entries(fieldsObj)) {
        if (excludedKeys.includes(fieldId)) {
            continue;
        }
        // Check string value
        if (fieldValue && typeof fieldValue === 'string') {
            if (isTelephoneCallStatusValue(fieldValue)) {
                return fieldValue;
            }
        }
        // Check array value (first element)
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
            const firstValue = fieldValue[0];
            if (typeof firstValue === 'string' && isTelephoneCallStatusValue(firstValue)) {
                return firstValue;
            }
        }
    }
    return null;
};

/**
 * Get telephone status from fields object
 * Extract value using field id from fieldListData or search by pattern
 * @param {Object} ticket - Ticket object
 * @returns {string|null} Telephone status or null
 */
const getTelephoneStatusFromFieldsObject = (ticket) => {
    if (!ticket.fields || Utils.isEmptyObject(ticket.fields)) {
        return null;
    }
    const fieldsObj = Utils.convertJSONDataToObject(ticket.fields);
    if (!fieldsObj || Utils.isEmptyObject(fieldsObj)) {
        return null;
    }
    // Try to find field id from fieldListData first
    const fieldList =
        Utils.convertJSONDataToObject(ticket.fieldListData) || ticket.listFieldData || [];
    if (!Utils.isEmptyArray(fieldList)) {
        const telephoneCallStatusField = findTelephoneCallStatusField(fieldList);
        if (telephoneCallStatusField && fieldsObj[telephoneCallStatusField.id]) {
            const fieldValue = fieldsObj[telephoneCallStatusField.id];
            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                return fieldValue[0];
            }
            if (!Utils.isEmptyString(fieldValue)) {
                return fieldValue;
            }
        }
    } else {
        // If no fieldListData, search through all field values
        return getTelephoneStatusFromFieldsObjectWithoutFieldList(fieldsObj);
    }
    return null;
};

/**
 * Get telephone call status from ticket object
 * Extract and normalize telephone call status from multiple data sources
 * Priority order:
 * 1. Direct properties (telephoneCallStatus, callStatus, ticketFieldData, status)
 * 2. FieldListData (ticketData array, field defaultValue/value)
 * 3. Fields object (using field id or pattern matching)
 * @param {Object} ticket - Ticket object
 * @returns {string|null} Telephone status or null if not found
 */
export const getTelephoneStatusFromTicket = (ticket) => {
    try {
        if (!ticket) {
            return null;
        }
        // Try direct properties first
        const directStatus = getTelephoneStatusFromDirectProperties(ticket);
        if (directStatus) {
            return directStatus;
        }
        // Try fieldListData
        const fieldListStatus = getTelephoneStatusFromFieldListData(ticket);
        if (fieldListStatus) {
            return fieldListStatus;
        }
        // Try fields object
        return getTelephoneStatusFromFieldsObject(ticket);
    } catch (error) {
        console.warn('Error getting telephone status from ticket:', error);
        return null;
    }
};

/**
 * Get hamburger menu options for a telephone ticket
 * Determine the list of options to display based on the telephone status and ticket state
 * @param {string} telephoneStatus - Telephone call status
 * @param {boolean} isArchived - Whether the ticket has been archived
 * @param {boolean} isDeleted - Whether the ticket has been deleted
 * @param {boolean} isHelpdeskPanel - Whether the panel type is Helpdesk
 * @returns {Array<string>} - Array of allowed options to display
 */
export const getHamburgerMenuOptionsForTelephone = (
    telephoneStatus,
    isArchived,
    isDeleted,
    isHelpdeskPanel
) => {
    try {
        if (isDeleted) {
            return [
                Constant.HAMBURGER_MENU_OPTION.CHAT,
                Constant.HAMBURGER_MENU_OPTION.PERMANENT_DELETE
            ];
        }

        if (isArchived) {
            if (isHelpdeskPanel) {
                return [Constant.HAMBURGER_MENU_OPTION.CHAT, Constant.HAMBURGER_MENU_OPTION.DELETE];
            } else {
                return [Constant.HAMBURGER_MENU_OPTION.CHAT];
            }
        }

        if (!telephoneStatus) {
            return [Constant.HAMBURGER_MENU_OPTION.CHAT];
        }

        if (
            telephoneStatus === TelephoneConstant.TELEPHONE_CALL_STATUS.CALL_WAITING ||
            telephoneStatus === TelephoneConstant.TELEPHONE_CALL_STATUS.DURING_CALL
        ) {
            return [Constant.HAMBURGER_MENU_OPTION.CHAT];
        }

        if (
            telephoneStatus === TelephoneConstant.TELEPHONE_CALL_STATUS.END_CALL ||
            telephoneStatus === TelephoneConstant.TELEPHONE_CALL_STATUS.MISSING_CALL ||
            telephoneStatus === TelephoneConstant.TELEPHONE_CALL_STATUS.CANCELLED_CALL
        ) {
            return [
                Constant.HAMBURGER_MENU_OPTION.CHAT,
                Constant.HAMBURGER_MENU_OPTION.ARCHIVE,
                Constant.HAMBURGER_MENU_OPTION.DELETE
            ];
        }
        // Default fallback
        return [Constant.HAMBURGER_MENU_OPTION.CHAT];
    } catch (error) {
        console.warn('Error getting hamburger menu options for telephone:', error);
        return [Constant.HAMBURGER_MENU_OPTION.CHAT];
    }
};
